import { useEffect, useMemo, useState } from 'react';
import { type GraphInfo, type GraphKind, graphDisplayLabel, useGraphs } from '@/lib/useGraphs';
import { cn } from '@/lib/utils';

/** Sentinel option value for "create a new named graph". */
const CREATE_OPTION = '__create_graph__';

export interface GraphSelectorProps {
  /** Current selection — bare IRI form (`urn:tres:ontology`). */
  value: string | null;
  /** Called with the bare IRI when the user picks a graph. */
  onChange: (bare: string) => void;
  /**
   * Restrict to managed graphs only. Use on the upload page where
   * loading to a custom URI would defeat the 3-graph methodology.
   * Defaults to false (read mode — show all named graphs).
   */
  managedOnly?: boolean;
  /**
   * Offer a "＋ New graph…" option that reveals a URI input, so a load can
   * target a brand-new custom named graph. Suppresses the "All graphs" union
   * (a load goes to exactly one graph). Use on the load page.
   */
  allowCreate?: boolean;
  /**
   * The graph the host page opens on when `value` is null and nothing is
   * stored under `storageKey`. A page may open its picker on the graph that
   * fits its subject (Ch08 §2.7); with none given, the picker opens empty and
   * nothing substitutes for the user's pick.
   */
  defaultBare?: string;
  /**
   * LocalStorage key for last-used persistence. When provided, the
   * selector persists the user's choice on change and restores on
   * mount when `value` is null. Pass a stable per-page key like
   * `graphSelector.adminLoad` so each page tracks its own last-used.
   */
  storageKey?: string;
  /** Optional inline label, defaults to "Graph". */
  label?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Reusable graph selector backed by `/api/v1/graphs`.
 *
 * Lists the three managed graphs first (Ontology / Vocabulary / Data),
 * with friendly labels, then any user-named graphs alphabetically.
 * In `managedOnly` mode (upload page), only the three managed graphs
 * are offered.
 *
 * The component does not read the graph state itself — the host page
 * owns it via `value` / `onChange`. LocalStorage persistence is
 * opt-in via `storageKey`.
 */
export function GraphSelector(props: GraphSelectorProps) {
  const {
    value,
    onChange,
    managedOnly = false,
    allowCreate = false,
    defaultBare,
    storageKey,
    label = 'Graph',
    className,
    disabled = false,
  } = props;
  const graphs = useGraphs();
  // In create mode, whether the "New graph…" input is showing, and what has
  // been typed there — a plain name is accepted and becomes an IRI.
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');

  // On first mount with no value: the user's own last pick under this page's
  // key, else the graph the page opens on, else nothing. The component never
  // invents a graph of its own (74.3).
  useEffect(() => {
    if (value !== null) return;
    if (storageKey) {
      let stored: string | null = null;
      try {
        stored = window.localStorage.getItem(storageKey);
      } catch {
        stored = null;
      }
      if (stored && stored.length > 0) {
        onChange(stored);
        return;
      }
    }
    if (defaultBare) onChange(defaultBare);
    // The intent is "run once on mount when value is null". Including
    // onChange / defaultBare / storageKey would re-fire on every change
    // and stomp on the user's selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo<GraphInfo[]>(() => {
    const all = graphs.data ?? [];
    if (managedOnly) {
      return all.filter((g) => g.managed);
    }
    // The load page (allowCreate) targets exactly one graph — no union; the
    // read pages prepend the "All graphs" union (ARQ's union-of-named-graphs)
    // so cross-graph queries read across every graph.
    if (allowCreate) {
      return all;
    }
    const union: GraphInfo = {
      uri: `<${ALL_GRAPHS}>`,
      bare: ALL_GRAPHS,
      kind: null,
      managed: false,
    };
    return [union, ...all];
  }, [graphs.data, managedOnly, allowCreate]);

  const handleChange = (bare: string) => {
    if (bare === CREATE_OPTION) {
      setCreating(true);
      onChange('');
      return;
    }
    setCreating(false);
    onChange(bare);
    if (storageKey) {
      try {
        window.localStorage.setItem(storageKey, bare);
      } catch {
        // Quota errors / private mode — non-fatal.
      }
    }
  };

  return (
    <label className={cn('inline-flex items-center gap-2 text-sm', className)}>
      {label && <span className="font-medium text-muted-foreground">{label}</span>}
      <select
        value={creating ? CREATE_OPTION : (value ?? '')}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled || graphs.isLoading}
        className={cn(
          'rounded-md border border-input bg-background px-2 py-1 text-sm',
          'focus:outline-none focus:ring-2 focus:ring-ring',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        {value === null && !creating && <option value="">Select a graph…</option>}
        {options.map((g) => (
          <option key={g.bare} value={g.bare} title={g.bare}>
            {optionText(g)}
          </option>
        ))}
        {allowCreate && <option value={CREATE_OPTION}>＋ New graph…</option>}
      </select>
      {creating && (
        <input
          type="text"
          autoFocus
          spellCheck={false}
          placeholder="a name, or a full urn: / http(s):// IRI"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            onChange(newGraphIri(e.target.value));
          }}
          disabled={disabled}
          className={cn(
            'w-72 rounded-md border border-input bg-background px-2 py-1 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring',
          )}
        />
      )}
      {graphs.isLoading && (
        <span className="text-xs text-muted-foreground">loading…</span>
      )}
      {graphs.error && (
        <span className="text-xs text-destructive">load failed</span>
      )}
    </label>
  );
}

/** What a typed graph name becomes. A full IRI (`urn:` or `http(s)://`) is
 *  kept as typed; a plain name such as `LoadTest` becomes
 *  `urn:tres:graph:LoadTest` — the shape the server accepts, so a person
 *  never has to know the IRI rule to make a graph (UAT 2026-08-29). */
export function newGraphIri(typed: string): string {
  const t = typed.trim();
  if (t === '' || /^(urn:|https?:\/\/)/i.test(t)) return t;
  return `urn:tres:graph:${t.replace(/\s+/g, '-')}`;
}

function optionText(g: GraphInfo): string {
  if (g.bare === ALL_GRAPHS) {
    return 'All graphs';
  }
  // Managed graphs get their friendly label; custom graphs collapse to their
  // local name (full URI lives in the option's title tooltip).
  return graphDisplayLabel(g);
}

/** ARQ's union-of-all-named-graphs pseudo graph. */
export const ALL_GRAPHS = 'urn:x-arq:UnionGraph';

/** Convenience: the bare IRI form of the managed graphs. */
export const MANAGED_GRAPH_KINDS: ReadonlyArray<{ bare: string; kind: GraphKind }> = [
  { bare: 'urn:tres:ontology', kind: 'ontology' },
  { bare: 'urn:tres:vocabulary', kind: 'vocabulary' },
  { bare: 'urn:tres:data', kind: 'data' },
  { bare: 'urn:tres:catalog', kind: 'catalog' },
];

/** Maps a kind string to the corresponding bare IRI. */
export function graphForKind(kind: 'ontology' | 'vocabulary' | 'data' | 'catalog'): string {
  return `urn:tres:${kind}`;
}

/** Human name for a graph IRI: managed kinds get their title-case name,
 * customs fall back to the IRI's local part. */
export function graphDisplayName(bare: string): string {
  const kind = kindForBare(bare);
  if (kind) return kind.charAt(0).toUpperCase() + kind.slice(1);
  const local = bare.split(/[#/:]/).filter(Boolean).pop();
  return local ?? bare;
}

/** Inverse: maps a bare IRI to the kind string, or null for custom. */
export function kindForBare(bare: string): GraphKind {
  switch (bare) {
    case 'urn:tres:ontology':
      return 'ontology';
    case 'urn:tres:vocabulary':
      return 'vocabulary';
    case 'urn:tres:data':
      return 'data';
    case 'urn:tres:catalog':
      return 'catalog';
    default:
      return null;
  }
}
